/**
 * Itens da requisição (ADR-0078).
 *
 * - Requisição que JUNTOU outras mostra os itens em blocos, um por
 *   requisição de origem (número, quem pediu, quando, justificativa), com
 *   "Desfazer junção" no bloco. Dá para alternar para "por item": itens
 *   iguais ficam lado a lado, com o subtotal — juntos, nunca somados.
 * - Cada item: obra, anexos (até 5, qualquer tipo de item) e a anotação
 *   livre de entrega/retirada do comprador.
 */
import React, { useMemo, useRef, useState } from "react";
import { FiEye, FiPaperclip, FiRotateCcw, FiX } from "react-icons/fi";
import { Link } from "react-router-dom";
import styled from "styled-components";

import ConfirmDialog from "../../../components/ConfirmDialog";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import {
  EXTENSOES_DE_ANEXO, MAX_ANEXOS_POR_ITEM, anexarNoItem, desfazerConsolidacao,
  editarItemRequisicao, removerAnexoDoItem,
} from "../../../services/compras";
import { fmtQt } from "../../RevisorFerragens/lib/documento";
import { Mono } from "../_shared/quadro/styles";
import { Rotulo, Segmented, TagOrigem } from "./ui";
import VisualizadorDeAnexo from "./VisualizadorDeAnexo";

const Grupo = styled.div`
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--surface);
  overflow: hidden;
  > .grupo-cab {
    display: flex; flex-wrap: wrap; align-items: center; gap: 6px 10px;
    padding: 8px 14px; font-size: 12px; min-width: 0;
    background: var(--surface-2); border-bottom: 1px solid var(--border-subtle);
  }
  /* Linhas baixas (pedido da gestora, 2026-10-02): mais itens na tela sem rolar tanto. */
  > .itens > div.item { display: flex; flex-wrap: wrap; gap: 4px 14px; align-items: center; padding: 6px 12px; border-bottom: 1px solid var(--border-subtle); }
  > .itens > div.item:last-child { border-bottom: 0; }
`;

const AGRUPAR_ORIGEM = "origem";
const AGRUPAR_ITEM = "item";
const PROPRIA = "__propria__";

const CAMPO = {
  height: 30, padding: "0 8px", borderRadius: 8, border: "1px solid var(--border)",
  background: "var(--surface)", color: "var(--ink)", font: "inherit", fontSize: 12, width: "100%", boxSizing: "border-box",
};

function dataHora(iso) {
  return iso ? new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";
}

export function nomeDoItem(it) {
  return it.produto ? (it.produto_nome || "—") : (it.descricao || "—");
}

/** Blocos por requisição de origem; o primeiro é o da própria requisição. */
export function gruposPorOrigem(req) {
  const origens = new Map((req.origens || []).map((o) => [o.id, o]));
  const grupos = new Map();
  (req.itens || []).forEach((it) => {
    const chave = it.requisicao_origem || PROPRIA;
    if (!grupos.has(chave)) grupos.set(chave, []);
    grupos.get(chave).push(it);
  });
  const saida = [];
  if (grupos.has(PROPRIA)) saida.push({ chave: PROPRIA, origem: null, itens: grupos.get(PROPRIA) });
  [...grupos.keys()].filter((k) => k !== PROPRIA)
    .map((k) => ({ chave: k, origem: origens.get(k) || { id: k, numero: grupos.get(k)[0].requisicao_origem_numero }, itens: grupos.get(k) }))
    .sort((a, b) => String(a.origem.criada_em || "").localeCompare(String(b.origem.criada_em || "")))
    .forEach((g) => saida.push(g));
  return saida;
}

/** Itens iguais lado a lado, com o subtotal do grupo (nunca somados). */
export function gruposPorItem(req) {
  const grupos = new Map();
  (req.itens || []).forEach((it) => {
    const chave = it.chave_igualdade || it.id;
    if (!grupos.has(chave)) grupos.set(chave, []);
    grupos.get(chave).push(it);
  });
  return [...grupos.entries()].map(([chave, itens]) => ({
    chave,
    itens,
    total: itens.filter((i) => i.situacao !== "CANCELADO")
      .reduce((s, i) => s + Number(i.qtd_aprovada || i.qtd_solicitada || 0), 0),
  }));
}

function AnexosDoItem({ req, item, podeEditar, onMudou }) {
  const { request } = useAuth();
  const toast = useToast();
  const entrada = useRef(null);
  const [enviando, setEnviando] = useState(false);
  const [aberto, setAberto] = useState(null);
  const anexos = item.anexos || [];

  async function enviar(arquivos) {
    const lista = Array.from(arquivos || []).slice(0, MAX_ANEXOS_POR_ITEM - anexos.length);
    if (!lista.length) return;
    setEnviando(true);
    try {
      // Um arquivo por chamada: é o padrão de upload do projeto.
      for (const arquivo of lista) {
        // eslint-disable-next-line no-await-in-loop
        await anexarNoItem(request, req.id, item.id, arquivo);
      }
      await onMudou();
    } catch (e) {
      toast.error(e?.payload?.detalhe || e?.message || "Não foi possível anexar.");
      await onMudou();
    } finally {
      setEnviando(false);
    }
  }

  async function remover(anexo) {
    try {
      await removerAnexoDoItem(request, req.id, item.id, anexo.id);
      await onMudou();
    } catch (e) {
      toast.error(e?.payload?.detalhe || e?.message || "Não foi possível remover o anexo.");
    }
  }

  if (!anexos.length && !podeEditar) return null;
  return (
    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginTop: 4, fontSize: 12 }}>
      {anexos.map((a) => (
        <span key={a.id || a.nome} style={{ display: "inline-flex", gap: 4, alignItems: "center", minWidth: 0 }}>
          <button type="button" onClick={() => setAberto(a)} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--accent-text, var(--accent))", font: "inherit", display: "inline-flex", gap: 4, alignItems: "center", minWidth: 0 }} title={`Ver ${a.nome}`}>
            <FiEye size={12} />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 180 }}>{a.nome}</span>
          </button>
          {podeEditar && a.id ? (
            <button type="button" onClick={() => remover(a)} aria-label={`Remover anexo ${a.nome}`} style={{ background: "none", border: "none", color: "var(--ink-4)", cursor: "pointer", padding: 0, display: "inline-flex" }}>
              <FiX size={12} />
            </button>
          ) : null}
        </span>
      ))}
      {podeEditar && anexos.length < MAX_ANEXOS_POR_ITEM ? (
        <>
          <button type="button" disabled={enviando} onClick={() => entrada.current?.click()} style={{ background: "none", border: "none", color: "var(--accent)", cursor: "pointer", padding: 0, display: "inline-flex", gap: 4, alignItems: "center", font: "inherit", fontSize: 12 }}>
            <FiPaperclip size={12} /> {enviando ? "Enviando…" : `Anexar (${anexos.length}/${MAX_ANEXOS_POR_ITEM})`}
          </button>
          <input ref={entrada} type="file" multiple hidden accept={EXTENSOES_DE_ANEXO} aria-label={`Anexar arquivos em ${nomeDoItem(item)}`} onChange={(e) => { enviar(e.target.files); e.target.value = ""; }} />
        </>
      ) : null}
      {aberto ? (
        <VisualizadorDeAnexo
          anexo={aberto}
          cabecalho={{ titulo: `${req.numero} · ${nomeDoItem(item)}`, detalhe: aberto.nome }}
          onFechar={() => setAberto(null)}
        />
      ) : null}
    </div>
  );
}

/** Texto livre por item: "Juninho retira 17/08", "falta 1 chapa". */
function AnotacaoDeEntrega({ req, item, podeEditar, onMudou }) {
  const { request } = useAuth();
  const toast = useToast();
  const [texto, setTexto] = useState(item.entrega_obs || "");
  const [editando, setEditando] = useState(false);

  async function salvar() {
    setEditando(false);
    if (texto.trim() === (item.entrega_obs || "")) return;
    try {
      await editarItemRequisicao(request, req.id, item.id, { entrega_obs: texto.trim() });
      await onMudou();
    } catch (e) {
      setTexto(item.entrega_obs || "");
      toast.error(e?.payload?.detalhe || e?.message || "Não foi possível salvar a anotação.");
    }
  }

  if (!podeEditar) {
    return item.entrega_obs ? <div style={{ fontSize: 12, color: "var(--ink-2)", marginTop: 4 }}>Entrega/retirada: {item.entrega_obs}</div> : null;
  }
  if (!editando) {
    return (
      <button type="button" onClick={() => setEditando(true)} style={{ background: "none", border: "none", padding: 0, marginTop: 4, font: "inherit", fontSize: 12, cursor: "pointer", color: item.entrega_obs ? "var(--ink-2)" : "var(--ink-4)", textAlign: "left" }}>
        {item.entrega_obs ? `Entrega/retirada: ${item.entrega_obs}` : "+ Anotar entrega/retirada"}
      </button>
    );
  }
  return (
    <input
      autoFocus
      value={texto}
      maxLength={300}
      onChange={(e) => setTexto(e.target.value)}
      onBlur={salvar}
      onKeyDown={(e) => { if (e.key === "Enter") salvar(); if (e.key === "Escape") { setTexto(item.entrega_obs || ""); setEditando(false); } }}
      placeholder="Ex.: Juninho retira 17/08 · falta 1 chapa · 4x no boleto"
      aria-label={`Entrega ou retirada de ${nomeDoItem(item)}`}
      style={{ ...CAMPO, marginTop: 4 }}
    />
  );
}

function CabecalhoDaOrigem({ req, grupo, podeConsolidar, onDesfazer }) {
  const o = grupo.origem;
  if (!o) {
    return (
      <div className="grupo-cab">
        <Mono $size={12}>{req.numero}</Mono>
        <span style={{ color: "var(--ink-3)" }}>itens desta requisição · {grupo.itens.length}</span>
      </div>
    );
  }
  return (
    <div className="grupo-cab">
      <Link to={`/suprimentos/requisicoes/${o.id}`}><Mono $size={12}>{o.numero}</Mono></Link>
      {o.origem ? <TagOrigem origem={o.origem} label={o.origem_label} curta /> : null}
      <span style={{ color: "var(--ink-3)", minWidth: 0 }}>
        {[o.solicitado_por_nome, o.criada_em ? dataHora(o.criada_em) : "", o.cliente_nome].filter(Boolean).join(" · ")}
        {o.prioridade === "URGENTE" ? " · urgente" : ""}
        {` · ${grupo.itens.length} ${grupo.itens.length === 1 ? "item" : "itens"}`}
      </span>
      <span style={{ flex: 1 }} />
      {podeConsolidar ? (
        <button
          type="button"
          className="btn btn--secondary btn--sm"
          disabled={!o.pode_desfazer}
          title={o.pode_desfazer ? "Devolve estes itens à requisição de origem, com as cotações" : o.motivo_nao_desfazer}
          onClick={() => onDesfazer(o)}
        >
          <FiRotateCcw size={12} /> Desfazer junção
        </button>
      ) : null}
      {o.justificativa ? <div style={{ flexBasis: "100%", color: "var(--ink-3)", whiteSpace: "pre-line" }}>{o.justificativa}</div> : null}
    </div>
  );
}

export default function ItensDaRequisicao({ req, podeGerir, podeConsolidar, onRecarregar, renderLinha }) {
  const { request } = useAuth();
  const toast = useToast();
  const temOrigens = (req.origens || []).length > 0 || (req.itens || []).some((i) => i.requisicao_origem);
  const temIguais = useMemo(() => gruposPorItem(req).some((g) => g.itens.length > 1), [req]);
  const [agrupar, setAgrupar] = useState(AGRUPAR_ORIGEM);
  const [desfazendo, setDesfazendo] = useState(null);
  const podeEditar = podeGerir && !["CANCELADA", "CONSOLIDADA"].includes(req.situacao);

  async function desfazer() {
    const origem = desfazendo;
    setDesfazendo(null);
    try {
      await desfazerConsolidacao(request, req.id, origem.id);
      toast.success(`Junção desfeita: os itens voltaram para ${origem.numero}.`);
      await onRecarregar();
    } catch (e) {
      toast.error(e?.payload?.detalhe || e?.message || "Não foi possível desfazer a junção.");
    }
  }

  const extras = (it) => (
    <>
      {it.cliente_nome ? <div style={{ fontSize: 12, color: "var(--ink-3)" }}>Obra: {it.cliente_nome}</div> : null}
      <AnexosDoItem req={req} item={it} podeEditar={podeEditar} onMudou={onRecarregar} />
      <AnotacaoDeEntrega key={`${it.id}:${it.entrega_obs || ""}`} req={req} item={it} podeEditar={podeEditar} onMudou={onRecarregar} />
    </>
  );

  const porOrigem = agrupar === AGRUPAR_ORIGEM || !temIguais;
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 6 }}>
        <Rotulo style={{ margin: 0 }}>Itens ({(req.itens || []).length})</Rotulo>
        <span style={{ flex: 1 }} />
        {temOrigens && temIguais ? (
          <Segmented
            ariaLabel="Agrupar itens"
            opcoes={[{ value: AGRUPAR_ORIGEM, label: "Por requisição de origem" }, { value: AGRUPAR_ITEM, label: "Itens iguais juntos" }]}
            valor={agrupar}
            onChange={setAgrupar}
          />
        ) : null}
      </div>

      {porOrigem ? gruposPorOrigem(req).map((g) => (
        <Grupo key={g.chave} style={{ marginBottom: temOrigens ? 12 : 0 }}>
          {temOrigens ? <CabecalhoDaOrigem req={req} grupo={g} podeConsolidar={podeConsolidar} onDesfazer={setDesfazendo} /> : null}
          <div className="itens">{g.itens.map((it) => renderLinha(it, extras(it)))}</div>
        </Grupo>
      )) : gruposPorItem(req).map((g) => (
        <Grupo key={g.chave} style={{ marginBottom: 12 }}>
          {g.itens.length > 1 ? (
            <div className="grupo-cab">
              <strong>{nomeDoItem(g.itens[0])}</strong>
              <span style={{ color: "var(--ink-3)" }}>
                {g.itens.length} linhas · total <Mono $size={12}>×{fmtQt(g.total)}</Mono> (não somadas: cada uma segue com a sua origem)
              </span>
            </div>
          ) : null}
          <div className="itens">
            {g.itens.map((it) => renderLinha(it, (
              <>
                {it.requisicao_origem_numero ? <div style={{ fontSize: 12, color: "var(--ink-3)" }}>Veio de {it.requisicao_origem_numero}</div> : null}
                {extras(it)}
              </>
            )))}
          </div>
        </Grupo>
      ))}

      <ConfirmDialog
        open={Boolean(desfazendo)}
        titulo={`Desfazer a junção de ${desfazendo?.numero || ""}?`}
        descricao={`Os itens que vieram de ${desfazendo?.numero || ""} voltam para ela como estão, com as cotações já feitas. A situação das duas requisições é recalculada.`}
        confirmarLabel="Desfazer junção"
        onCancelar={() => setDesfazendo(null)}
        onConfirmar={desfazer}
      />
    </div>
  );
}
